<template>
  <CurrentEventDataProvider v-slot="{ data }">
    <div class="event-badge-outer">
      <!-- White card header -->
      <div class="outer-header">
        <span class="outer-header__title">{{
          $t('Views.Lobby.EventBadge.eventBadge')
        }}</span>
        <button
          type="button"
          class="outer-header__icon outer-header__close"
          :aria-label="$t('Components.shared.Navbar.close')"
          @click="$emit('close')"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- Teal inner badge card — centred: role, QR, name, event -->
      <div ref="badgeCard" class="badge-card">
        <span class="badge-card__role">{{
          $t('Views.Lobby.EventBadge.attendee')
        }}</span>

        <!-- Badge / QR — the focal element -->
        <div class="badge-card__qr-wrap">
          <div
            class="badge-card__qr"
            v-if="isQrEnabled && profile.TicketQrCode"
          >
            <qrcode-vue
              :value="profile.TicketQrCode"
              :size="150"
              render-as="svg"
              level="H"
            />
          </div>
          <div v-else class="badge-card__qr badge-card__qr--placeholder">
            <i
              class="fas fa-qrcode"
              style="font-size: 64px; color: rgba(255, 255, 255, 0.35)"
            ></i>
          </div>
        </div>

        <!-- Participant name + event name -->
        <div class="badge-card__name">
          {{ profile.Firstname }} {{ profile.Lastname }}
        </div>
        <div class="badge-card__event" v-if="eventName">{{ eventName }}</div>

        <!-- Badge ID footer -->
        <div class="badge-card__footer">
          <span class="badge-card__footer-label">{{
            $t('Views.Lobby.EventBadge.badgeId')
          }}</span>
          <span class="badge-card__footer-value">{{ badgeId }}</span>
        </div>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { mapGetters } from 'vuex';
import QrcodeVue from 'qrcode.vue';

export default {
  name: 'EventBadge',
  components: { QrcodeVue },
  computed: {
    ...mapGetters('Authentication', { profile: 'getUser' }),
    ...mapGetters('CurrentEvent', ['getEvent', 'features']),
    eventName() {
      return (this.getEvent && this.getEvent.eventName) || '';
    },
    badgeId() {
      return (
        (this.profile && (this.profile.BadgeId || this.profile.TicketType)) ||
        'ATD-ENT'
      );
    },
    isQrEnabled() {
      return !!(
        this.profile &&
        this.profile.TicketQrCode &&
        this.features?.stageQrTicketsEnabled
      );
    }
  },
  methods: {
    downloadBadge() {
      // Open a fresh window containing only the badge card so the browser
      // print dialog targets just the badge — not the full lobby page.
      const cardEl = this.$refs.badgeCard;
      if (!cardEl) return;
      const cardHtml = cardEl.outerHTML;

      const printWindow = window.open('', '_blank', 'width=720,height=560');
      if (!printWindow) {
        // Popup blocked — fall back to the full-page print so the user
        // still gets a printable result.
        window.print();
        return;
      }

      // Self-contained HTML doc — no app CSS, no navbar, no lobby chrome.
      // The badge styles are inlined so the printed output matches the
      // teal card design exactly regardless of host browser print rules.
      // The print window has no app CSS, so read the live brand fill from
      // <html> (helpers/brand-theme.js) and inline it.
      const rootStyle = getComputedStyle(document.documentElement);
      const badgeBg =
        rootStyle.getPropertyValue('--brand-primary').trim() || '#047873';
      const badgeText =
        rootStyle.getPropertyValue('--brand-on-primary').trim() || '#ffffff';

      printWindow.document.open();
      printWindow.document.write(
        `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${this.$t('Views.Lobby.EventBadge.eventBadge')}</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #ffffff;
    }
    .print-frame {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 32px 24px;
      min-height: 100vh;
    }
    .badge-card {
      background: ${badgeBg};
      border-radius: 14px;
      padding: 28px;
      color: ${badgeText};
      width: 100%;
      max-width: 560px;
    }
    .badge-card__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 18px;
    }
    .badge-card__role {
      font-size: 14px;
      opacity: 0.7;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding-top: 4px;
    }
    .badge-card__qr {
      background: #ffffff;
      border-radius: 8px;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .badge-card__qr svg,
    .badge-card__qr canvas,
    .badge-card__qr img {
      display: block;
      width: 96px !important;
      height: 96px !important;
    }
    .badge-card__qr--placeholder {
      width: 110px;
      height: 110px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .badge-card__name {
      font-size: 24px;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 4px;
    }
    .badge-card__company {
      font-size: 15px;
      opacity: 0.75;
      margin-bottom: 18px;
    }
    .badge-card__divider {
      border: none;
      border-top: 1px solid rgba(255, 255, 255, 0.25);
      margin: 0 0 16px;
    }
    .badge-card__ids {
      display: flex;
      justify-content: space-between;
    }
    .badge-card__id-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .badge-card__id-item--right { text-align: right; }
    .badge-card__id-label {
      font-size: 12px;
      opacity: 0.65;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .badge-card__id-value {
      font-size: 14px;
      font-weight: 700;
    }
    @media print {
      .print-frame { padding: 0; min-height: auto; }
      .badge-card { box-shadow: none; }
      @page { margin: 12mm; }
    }
  </style>
</head>
<body>
  <div class="print-frame">${cardHtml}</div>
  <` +
          `script>
    window.addEventListener('load', function () {
      // Defer slightly so SVG QR code is fully parsed before print
      setTimeout(function () {
        window.focus();
        window.print();
      }, 150);
    });
    window.addEventListener('afterprint', function () {
      window.close();
    });
  <` +
          `/script>
</body>
</html>`
      );
      printWindow.document.close();
    }
  }
};
</script>

<style scoped lang="scss">
.event-badge-outer {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
}

.outer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
  }

  &__close {
    border: none;
    padding: 0;
    cursor: pointer;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 15px;
  }
}

.badge-card {
  background: var(--color-primary, #047873);
  border-radius: 16px;
  padding: 24px 22px;
  color: #ffffff;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &__role {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.9;
    background: rgba(255, 255, 255, 0.15);
    padding: 4px 14px;
    border-radius: 9999px;
    margin-bottom: 20px;
  }

  &__qr-wrap {
    margin-bottom: 20px;
  }

  &__qr {
    background: #ffffff;
    border-radius: 12px;
    padding: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);

    svg,
    canvas,
    img {
      display: block;
    }

    &--placeholder {
      width: 174px;
      height: 174px;
      background: rgba(255, 255, 255, 0.1);
      box-shadow: none;
    }
  }

  &__name {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.25;
  }

  &__event {
    font-size: 14px;
    opacity: 0.85;
    margin-top: 6px;
    line-height: 1.4;
  }

  &__footer {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
  }

  &__footer-label {
    font-size: 11px;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  &__footer-value {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
}

.download-badge-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px;
  background: var(--color-primary, #047873);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
